import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  verifyPasswordResetToken,
  completePasswordReset,
} from "@/lib/password-reset.functions";

type Search = { token?: string };

export const Route = createFileRoute("/nastavi-geslo")({
  head: () => ({
    meta: [
      { title: "Novo geslo — kupish.si" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://kupish.si/nastavi-geslo" }],
  }),
  validateSearch: (s: Record<string, unknown>): Search => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
  component: Page,
});

type Status = "checking" | "ready" | "invalid" | "used" | "expired" | "done";

function Page() {
  const { token } = useSearch({ from: "/nastavi-geslo" });
  const navigate = useNavigate();
  const verify = useServerFn(verifyPasswordResetToken);
  const complete = useServerFn(completePasswordReset);

  const [status, setStatus] = useState<Status>("checking");
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setStatus("invalid");
        return;
      }
      try {
        const res = await verify({ data: { token } });
        if (cancelled) return;
        if (res.valid) {
          setAccountEmail(res.email);
          setStatus("ready");
        } else {
          setStatus(res.reason === "expired" ? "expired" : res.reason === "used" ? "used" : "invalid");
        }
      } catch {
        if (!cancelled) setStatus("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, verify]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Geslo mora imeti vsaj 8 znakov.");
      return;
    }
    if (password !== confirm) {
      setError("Gesli se ne ujemata.");
      return;
    }
    if (!token) {
      setError("Manjka žeton za ponastavitev.");
      return;
    }
    setLoading(true);
    try {
      const res = await complete({ data: { token, password } });
      setLoading(false);
      if (!res.ok) {
        if (res.reason === "expired") setStatus("expired");
        else if (res.reason === "used") setStatus("used");
        else if (res.reason === "not_found") setStatus("invalid");
        else setError("Ponastavitev ni uspela. Poskusite znova.");
        return;
      }
      setStatus("done");
      setTimeout(() => navigate({ to: "/prijava", replace: true }), 1500);
    } catch (err) {
      setLoading(false);
      setError((err as Error).message || "Napaka pri ponastavitvi gesla.");
    }
  }

  return (
    <section className="mx-auto max-w-md px-6 py-24">
      <div className="eyebrow">Novo geslo</div>
      <h1 className="font-serif text-5xl mt-3">Nastavite geslo</h1>

      {status === "checking" && (
        <p className="mt-6 text-sm text-muted-foreground">Preverjam povezavo za ponastavitev…</p>
      )}

      {(status === "invalid" || status === "expired" || status === "used") && (
        <div className="mt-10 border border-border p-6 text-sm space-y-3">
          <p>
            {status === "expired"
              ? "Povezava za ponastavitev gesla je potekla."
              : status === "used"
              ? "Ta povezava za ponastavitev gesla je bila že uporabljena."
              : "Povezava za ponastavitev gesla je neveljavna."}
          </p>
          <p>
            <Link to="/pozabljeno-geslo" className="underline underline-offset-4 hover:text-accent">
              Zahtevajte novo povezavo
            </Link>
          </p>
        </div>
      )}

      {status === "done" && (
        <div className="mt-10 border border-border p-6 text-sm space-y-3">
          <p>Geslo je bilo uspešno posodobljeno. Preusmerjam vas na prijavo…</p>
        </div>
      )}

      {status === "ready" && (
        <>
          <p className="mt-3 text-sm text-muted-foreground">
            Nastavljate novo geslo za račun{" "}
            <strong className="text-foreground">{accountEmail ?? "—"}</strong>.
          </p>
          <form className="mt-10 space-y-5" onSubmit={onSubmit}>
            <div>
              <label className="text-xs eyebrow">Novo geslo</label>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
              />
            </div>
            <div>
              <label className="text-xs eyebrow">Potrdite geslo</label>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
              />
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground py-3 text-sm hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "Shranjujem…" : "Shrani novo geslo"}
            </button>
          </form>
        </>
      )}
    </section>
  );
}
